import { Flex } from '@chakra-ui/react';
import { FormFieldCheckbox } from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import CheckBoxUpdateModal from './CheckBoxUpdateModal';
import { FieldHeader } from '../common/FieldHeader';
import { CheckBoxEmptyState } from './CheckBoxEmptyState';
import { CheckBoxDisplay } from './CheckBoxDisplay';
import { CheckBoxDiffModal } from './CheckBoxDiffModal';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

export type CheckBoxField = FormFieldCheckbox;

type IProps = {
  formFieldDefinition: CheckBoxField;
  value: boolean;
  previousValue?: boolean | null;
  onChange?: (value: boolean) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const GenericCheckBoxField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <CheckBoxUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: boolean) => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <CheckBoxDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          content={formFieldDefinition.params.labels.content}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {typeof value === 'boolean' ? (
        <CheckBoxDisplay
          value={value}
          content={formFieldDefinition.params.labels.content}
        />
      ) : (
        <CheckBoxEmptyState />
      )}
    </Flex>
  );
};
